import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, CheckCircle2, MessageSquare, AlertCircle, X,
  ClipboardList, ShieldCheck, Filter, ArrowRight, Eye,
} from "lucide-react";
import { useGetOpenExamDietsQuery } from "../../api/examsApi";
import {
  useChiefGetResultsQuery,
  useChiefApproveResultsMutation,
  useChiefRequestReviewMutation,
  useChiefForwardToExaminerMutation,
  useChiefGetBatchScoresQuery,
} from "../../api/chiefExaminerApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function extractError(err: any): string {
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

const STATUS_MAP: Record<string, string> = {
  submitted:         "bg-blue-50 text-blue-700 border-blue-100",
  under_review:      "bg-amber-50 text-amber-700 border-amber-100",
  approved:          "bg-emerald-50 text-emerald-700 border-emerald-100",
  under_moderation:  "bg-purple-50 text-purple-700 border-purple-100",
  moderated:         "bg-violet-50 text-violet-700 border-violet-100",
  moderation_rework: "bg-orange-50 text-orange-700 border-orange-100",
  examiner_rework:   "bg-rose-50 text-rose-700 border-rose-100",
  published:         "bg-emerald-50 text-emerald-700 border-emerald-100",
  rework:            "bg-rose-50 text-rose-700 border-rose-100",
};

type ActionPanel =
  | { type: "review"; id: string; comment: string }
  | { type: "forward"; id: string; comment: string }
  | { type: "scores"; id: string };

export default function ResultReviewPage() {
  const { data: diets = [], isLoading: dietsLoading } = useGetOpenExamDietsQuery();
  const [dietFilter, setDietFilter] = useState("");

  const { data: results = [], isLoading } = useChiefGetResultsQuery(
    dietFilter ? { dietId: dietFilter } : undefined
  );
  const [approveResults, { isLoading: approving }] = useChiefApproveResultsMutation();
  const [requestReview,  { isLoading: reviewing  }] = useChiefRequestReviewMutation();
  const [forwardToExaminer, { isLoading: forwarding }] = useChiefForwardToExaminerMutation();

  const { can } = useAccess();
  const mayDecide = can.perm(PERMS.exam.resultPublish);

  const [actionError, setActionError] = useState<Record<string, string>>({});
  const [panel, setPanel] = useState<ActionPanel | null>(null);

  const handleApprove = async (id: string) => {
    setActionError(p => ({ ...p, [id]: "" }));
    try { await approveResults(id).unwrap(); }
    catch (err) { setActionError(p => ({ ...p, [id]: extractError(err) })); }
  };

  const handleReview = async () => {
    if (!panel || panel.type !== "review") return;
    const { id, comment } = panel;
    setActionError(p => ({ ...p, [id]: "" }));
    if (!comment.trim()) {
      setActionError(p => ({ ...p, [id]: "Please add a comment explaining what needs rework." }));
      return;
    }
    try { await requestReview({ id, comment }).unwrap(); setPanel(null); }
    catch (err) { setActionError(p => ({ ...p, [id]: extractError(err) })); }
  };

  const handleForward = async () => {
    if (!panel || panel.type !== "forward") return;
    const { id, comment } = panel;
    setActionError(p => ({ ...p, [id]: "" }));
    if (!comment.trim()) {
      setActionError(p => ({ ...p, [id]: "Please add your instructions for the examiner." }));
      return;
    }
    try { await forwardToExaminer({ id, comment }).unwrap(); setPanel(null); }
    catch (err) { setActionError(p => ({ ...p, [id]: extractError(err) })); }
  };

  const selCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  const pending           = (results as any[]).filter((r: any) => ["submitted","under_review"].includes(r.status)).length;
  const awaitingForward   = (results as any[]).filter((r: any) => r.status === "moderation_rework").length;
  const approved          = (results as any[]).filter((r: any) => ["approved","published","under_moderation","moderated"].includes(r.status)).length;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Batches",        val: (results as any[]).length, icon: ClipboardList, color: "indigo"  },
          { label: "Pending Review",        val: pending,                   icon: AlertCircle,   color: "amber"   },
          { label: "Moderation Feedback",   val: awaitingForward,           icon: ArrowRight,    color: "orange"  },
          { label: "Approved / Published",  val: approved,                  icon: CheckCircle2,  color: "emerald" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <Link to="/exams/chief" className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </Link>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <ClipboardList className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Review Results</h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">Approve, request rework, or forward moderation feedback to examiners.</p>
            </div>
          </div>

          <div className="space-y-1 w-full sm:w-64">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
              <Filter className="w-3 h-3" /> Filter by Diet
            </label>
            {dietsLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
            ) : (
              <select value={dietFilter} onChange={(e) => setDietFilter(e.target.value)} className={selCls}>
                <option value="">All diets</option>
                {diets.map((d: any) => <option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Results list */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            All Batches {(results as any[]).length > 0 ? `(${(results as any[]).length})` : ""}
          </div>
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
          </div>
        ) : (results as any[]).length === 0 ? (
          <div className="py-24 text-center">
            <MessageSquare className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No results awaiting review</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {(results as any[]).map((r: any) => {
              const status     = (r.status ?? "submitted").toLowerCase();
              // Batch state says whether the act makes sense; mayDecide says
              // whether this account may perform it. The route needs only
              // exam.grade.access to READ the board — approving a batch, or
              // sending it back, sits behind exam.result.publish.
              const canApprove = mayDecide && (status === "submitted" || status === "under_review");
              const canForward = mayDecide && status === "moderation_rework";
              const showScores = panel?.type === "scores" && panel.id === r.id;
              return (
                <div key={r.id} className="group">
                  <div className="flex items-start justify-between px-8 py-5 hover:bg-gray-50/80 transition-all gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">
                        {r.course_name ?? `Course ${r.course_id}`}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                        Diet: {r.diet_name ?? r.diet_id} &nbsp;·&nbsp;
                        Examiner: {r.examiner_name ?? "—"} &nbsp;·&nbsp;
                        {r.student_count ?? 0} students
                      </div>

                      {/* Moderation feedback bubble */}
                      {canForward && (
                        <div className="mt-3 space-y-1.5">
                          {r.moderator_comment && (
                            <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-2.5 text-xs text-violet-800 font-medium">
                              <span className="font-black text-[9px] uppercase tracking-widest text-violet-500 block mb-0.5">Moderator Feedback</span>
                              {r.moderator_comment}
                            </div>
                          )}
                          {r.chief_moderator_comment && (
                            <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-2.5 text-xs text-orange-800 font-medium">
                              <span className="font-black text-[9px] uppercase tracking-widest text-orange-500 block mb-0.5">Chief Moderator Comment</span>
                              {r.chief_moderator_comment}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm shrink-0 ${STATUS_MAP[status] ?? "bg-gray-50 text-gray-400 border-gray-100"}`}>
                      {status.replace(/_/g, " ")}
                    </span>
                  </div>

                  {/* Action row */}
                  {(canApprove || canForward) && (
                    <div className="px-8 pb-5 flex flex-wrap items-center gap-3">
                      {actionError[r.id] && (
                        <p className="w-full text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> {actionError[r.id]}
                        </p>
                      )}

                      {canApprove && (
                        <>
                          <button onClick={() => handleApprove(String(r.id))} disabled={approving}
                            className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-emerald-600 transition-all shadow-sm disabled:opacity-30 active:scale-95">
                            {approving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            Approve & Forward
                          </button>
                          <button onClick={() => setPanel({ type: "review", id: String(r.id), comment: "" })}
                            className="inline-flex items-center gap-2 border border-amber-200 text-amber-700 rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-amber-50 transition-all active:scale-95">
                            <MessageSquare className="w-3.5 h-3.5" /> Request Rework
                          </button>
                        </>
                      )}

                      {canForward && (
                        <button onClick={() => setPanel({ type: "forward", id: String(r.id), comment: "" })}
                          className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-orange-600 transition-all shadow-sm active:scale-95">
                          <ArrowRight className="w-3.5 h-3.5" /> Forward to Examiner
                        </button>
                      )}

                      <button onClick={() => setPanel(showScores ? null : { type: "scores", id: String(r.id) })}
                        className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-gray-50 transition-all active:scale-95">
                        <Eye className="w-3.5 h-3.5" /> {showScores ? "Hide Scores" : "View Scores"}
                      </button>
                    </div>
                  )}

                  {/* Scores panel */}
                  {showScores && <ScoresPanel batchId={String(r.id)} />}

                  {/* Rework comment panel */}
                  {panel?.type === "review" && panel.id === String(r.id) && (
                    <CommentPanel
                      title="Rework Comment"
                      placeholder="Explain what needs to be corrected…"
                      colorClass="amber"
                      comment={panel.comment}
                      onCommentChange={(c) => setPanel(p => p ? { ...p, comment: c } : null)}
                      onSubmit={handleReview}
                      onCancel={() => setPanel(null)}
                      isLoading={reviewing}
                      submitLabel="Send Rework"
                    />
                  )}

                  {/* Forward comment panel */}
                  {panel?.type === "forward" && panel.id === String(r.id) && (
                    <CommentPanel
                      title="Instructions for Examiner"
                      placeholder="Summarise the moderation feedback and your instructions for score corrections…"
                      colorClass="orange"
                      comment={panel.comment}
                      onCommentChange={(c) => setPanel(p => p ? { ...p, comment: c } : null)}
                      onSubmit={handleForward}
                      onCancel={() => setPanel(null)}
                      isLoading={forwarding}
                      submitLabel="Forward to Examiner"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-8 h-8 text-indigo-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">Review Protocol</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Approve batches to forward to the Moderation Board. When the moderation board returns feedback
            (<span className="text-orange-400 font-bold">moderation rework</span>), review the comments and
            forward instructions to the examiner for score corrections.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────────

function ScoresPanel({ batchId }: { batchId: string }) {
  const { data: scores = [], isLoading } = useChiefGetBatchScoresQuery(batchId);

  const GRADE_COLOR: Record<string, string> = {
    A: "text-emerald-600", B: "text-emerald-500", C: "text-blue-500",
    D: "text-amber-500", E: "text-orange-500", F: "text-rose-500",
  };

  return (
    <div className="mx-8 mb-5 border border-gray-100 rounded-2xl overflow-hidden">
      <div className="px-5 py-3 bg-gray-50 text-[9px] font-black uppercase tracking-widest text-gray-400 flex justify-between">
        <span>Student Scores</span>
        {isLoading && <Loader2 className="w-3 h-3 animate-spin" />}
      </div>
      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-200" /></div>
      ) : scores.length === 0 ? (
        <div className="py-6 text-center text-[10px] font-black uppercase tracking-widest text-gray-300">No scores recorded</div>
      ) : (
        <div className="divide-y divide-gray-50 max-h-60 overflow-y-auto">
          {scores.map((s: any, i: number) => (
            <div key={i} className="flex items-center justify-between px-5 py-2.5 text-sm">
              <div>
                <div className="font-bold text-gray-800">{s.student_name ?? s.student_reg}</div>
                <div className="text-[10px] text-gray-400 font-mono">{s.student_reg}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold text-gray-700">{s.score}</span>
                <span className={`font-black text-base w-6 text-center ${GRADE_COLOR[s.grade] ?? "text-gray-500"}`}>{s.grade}</span>
                {s.version > 1 && <span className="text-[9px] bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full font-black">v{s.version}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentPanel({
  title, placeholder, colorClass, comment, onCommentChange, onSubmit, onCancel, isLoading, submitLabel,
}: {
  title: string; placeholder: string; colorClass: string;
  comment: string; onCommentChange: (c: string) => void;
  onSubmit: () => void; onCancel: () => void;
  isLoading: boolean; submitLabel: string;
}) {
  const c = colorClass;
  return (
    <div className={`mx-8 mb-5 p-5 bg-${c}-50 rounded-2xl border border-${c}-100 space-y-3`}>
      <div className="flex items-center justify-between">
        <p className={`text-[10px] font-black uppercase tracking-widest text-${c}-700`}>{title}</p>
        <button onClick={onCancel} className={`p-1.5 hover:bg-${c}-100 rounded-xl`}>
          <X className={`w-3.5 h-3.5 text-${c}-600`} />
        </button>
      </div>
      <textarea rows={3}
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        className={`w-full border-b-2 border-${c}-200 py-2 font-bold text-gray-800 outline-none focus:border-${c}-500 bg-transparent text-sm resize-none`}
        placeholder={placeholder}
      />
      <div className="flex gap-3">
        <button onClick={onSubmit} disabled={isLoading}
          className={`inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-${c}-600 transition-all disabled:opacity-30 active:scale-95`}>
          {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
          {isLoading ? "Sending…" : submitLabel}
        </button>
        <button onClick={onCancel}
          className="py-3 px-5 text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}
